import { z } from 'zod';

export const schedulingDepositPolicySchema = z
  .object({
    mode: z.enum(['NONE', 'REQUIRED', 'DEADLINE']),
    amountMode: z.enum(['FIXED', 'PERCENTAGE']),
    amount: z.number().int().min(0).max(1_000_000_000),
    afterRequestMinutes: z.number().int().min(1).max(525600).nullable(),
    beforeStartMinutes: z.number().int().min(0).max(525600).nullable(),
    expiryAction: z.enum(['KEEP_PENDING', 'CANCEL']),
  })
  .strict();
export type SchedulingDepositPolicy = z.infer<typeof schedulingDepositPolicySchema>;
export type SchedulingDepositSettings = {
  company: SchedulingDepositPolicy;
  units: Record<string, Partial<SchedulingDepositPolicy>>;
  /// Serviço com regra própria: substitui a da unidade e a da empresa por inteiro.
  services: Record<string, SchedulingDepositPolicy>;
};
export const defaultSchedulingDepositPolicy: SchedulingDepositPolicy = Object.freeze({
  mode: 'NONE',
  amountMode: 'FIXED',
  amount: 0,
  afterRequestMinutes: null,
  beforeStartMinutes: null,
  expiryAction: 'KEEP_PENDING',
});
export function resolveSchedulingDepositPolicy(
  settings: SchedulingDepositSettings,
  businessUnitId: string,
  serviceId?: string,
): SchedulingDepositPolicy {
  const service = serviceId ? settings.services[serviceId] : undefined;
  if (service) return schedulingDepositPolicySchema.parse(service);
  return schedulingDepositPolicySchema.parse({
    ...settings.company,
    ...settings.units[businessUnitId],
  });
}
export function reviewSchedulingDepositPolicy(policy: SchedulingDepositPolicy): string[] {
  const errors: string[] = [];
  if (policy.mode !== 'NONE' && policy.amount <= 0) errors.push('Informe um sinal maior que zero.');
  if (policy.amountMode === 'PERCENTAGE' && policy.amount > 10000)
    errors.push('O percentual não pode superar 100%.');
  if (
    policy.mode === 'DEADLINE' &&
    policy.afterRequestMinutes === null &&
    policy.beforeStartMinutes === null
  )
    errors.push('Configure pelo menos um limite para pagar o sinal.');
  return errors;
}
export function schedulingDepositRequirement(input: {
  policy: SchedulingDepositPolicy;
  priceCents: number;
  requestedAt: Date;
  startsAt: Date;
  prepaid: boolean;
}): {
  amountCents: number;
  dueAt: Date | null;
  expiryAction: SchedulingDepositPolicy['expiryAction'];
} {
  const { policy } = input;
  if (input.prepaid || policy.mode === 'NONE')
    return { amountCents: 0, dueAt: null, expiryAction: 'KEEP_PENDING' };
  const amountCents = Math.min(
    input.priceCents,
    policy.amountMode === 'FIXED'
      ? policy.amount
      : Math.ceil((input.priceCents * policy.amount) / 10000),
  );
  const limits: number[] = [];
  if (policy.mode === 'DEADLINE') {
    if (policy.afterRequestMinutes !== null)
      limits.push(input.requestedAt.getTime() + policy.afterRequestMinutes * 60000);
    if (policy.beforeStartMinutes !== null)
      limits.push(input.startsAt.getTime() - policy.beforeStartMinutes * 60000);
  }
  return {
    amountCents,
    dueAt: limits.length ? new Date(Math.min(...limits)) : null,
    expiryAction: policy.expiryAction,
  };
}
export type SchedulingNoticeKind =
  | 'REQUESTED'
  | 'QUEUE_POSITION'
  | 'DEPOSIT_INSTRUCTIONS'
  | 'TIME_OCCUPIED'
  | 'DEPOSIT_EXPIRED'
  | 'CREDIT_AVAILABLE';
export const schedulingNoticeLabels: Readonly<Record<SchedulingNoticeKind, string>> = Object.freeze(
  {
    REQUESTED: 'Solicitação recebida',
    QUEUE_POSITION: 'Posição na fila',
    DEPOSIT_INSTRUCTIONS: 'Instruções do sinal',
    TIME_OCCUPIED: 'Horário ocupado',
    DEPOSIT_EXPIRED: 'Prazo do sinal vencido',
    CREDIT_AVAILABLE: 'Crédito e remarcação',
  },
);
export interface SchedulingNotice {
  id: string;
  appointmentId: string;
  kind: SchedulingNoticeKind;
  label: string;
  createdAt: string;
  preparedAt: string | null;
  obsoleteAt: string | null;
  emailQueued: boolean;
}
